import React, { useEffect, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import PortalAlert from "./PortalAlert";

const EMPTY_FORM = {
  followupDate: "",
  followupType: "",
  nextFollowupDate: "",
  remarks: "",
};

function toDatetimeLocalValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value).trim().replace(" ", "T");
    return raw.length >= 16 ? raw.slice(0, 16) : raw;
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatFollowupDateTime(value) {
  if (!value) return null;
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  return withSeconds.replace("T", " ");
}

/**
 * Add / edit follow-up modal for portal real-estate leads.
 *
 * @param {object} props
 * @param {number|string} props.leadId
 * @param {object|null} props.followup - existing follow-up for edit; null/undefined for add
 * @param {() => void} props.onClose
 * @param {() => void|Promise<void>} props.onSaved
 * @param {string} [props.leadName]
 */
function PortalLeadFollowupModal({ leadId, followup = null, onClose, onSaved, leadName = "" }) {
  const editingId = followup?.Followup_Id || followup?.followup_id || null;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (followup) {
      setForm({
        followupDate: toDatetimeLocalValue(followup.Followup_Date || followup.followup_date),
        followupType: followup.Followup_Type || followup.followup_type || "",
        nextFollowupDate: toDatetimeLocalValue(
          followup.Next_Followup_Date || followup.next_followup_date
        ),
        remarks: followup.Remarks || followup.remarks || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError("");
  }, [followup, leadId]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.followupDate) {
      setError("Follow-up date is required.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      Followup_Date: formatFollowupDateTime(form.followupDate),
      Followup_Type: form.followupType.trim() || null,
      Remarks: form.remarks.trim() || null,
      Next_Followup_Date: formatFollowupDateTime(form.nextFollowupDate),
    };
    try {
      if (editingId) {
        await axiosInstance.put(`/client/leads/${leadId}/followups/${editingId}`, payload);
      } else {
        await axiosInstance.post(`/client/leads/${leadId}/followups`, payload);
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (editingId ? "Could not update follow-up." : "Could not add follow-up.")
      );
    } finally {
      setSaving(false);
    }
  };

  const title = editingId ? "Edit follow-up" : "Add follow-up";
  const subtitle = leadName ? ` for ${leadName}` : "";

  return (
    <div className="cportal-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="cportal-modal cportal-modal--form"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="portal-lead-followup-modal-title"
      >
        <div className="cportal-modal-header">
          <h3 id="portal-lead-followup-modal-title">
            {title}
            {subtitle}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form className="cportal-modal-body" onSubmit={handleSubmit}>
          <PortalAlert type="error" message={error} onClear={() => setError("")} />
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>
                Follow-up date <span className="cportal-required">*</span>
              </label>
              <input
                type="datetime-local"
                value={form.followupDate}
                onChange={(e) => setField("followupDate", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Follow-up type</label>
              <input
                type="text"
                value={form.followupType}
                onChange={(e) => setField("followupType", e.target.value)}
                placeholder="Call, Visit, Email…"
                maxLength={150}
              />
            </div>
            <div className="cportal-field">
              <label>Next follow-up date</label>
              <input
                type="datetime-local"
                value={form.nextFollowupDate}
                onChange={(e) => setField("nextFollowupDate", e.target.value)}
              />
            </div>
            <div className="cportal-field cportal-field--full">
              <label>Remarks</label>
              <textarea
                rows={3}
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
              />
            </div>
          </div>
          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Save changes" : "Save follow-up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PortalLeadFollowupModal;
